import { useState } from "react";
import { projects, projectFilters } from "@/data/content";
import { SEO, breadcrumbLd } from "@/components/site/SEO";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { CTASection } from "@/components/site/CTASection";
import { AnimatePresence, motion } from "framer-motion";

export default function OurWork() {
  const [filter, setFilter] = useState("All");
  const visible =
    filter === "All" ? projects : projects.filter((p) => p.tags.includes(filter));

  return (
    <>
      <SEO
        title="Our Work | Blinds & Shutters Inspiration Gallery | My Blinds Company"
        description="Browse blinds and shutters styles by room — living rooms, bedrooms, kitchens, bay windows, conservatories and motorised installations across Birmingham."
        path="/our-work"
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Our Work", path: "/our-work" },
          ]),
        ]}
      />
      <section className="bg-warm-50 grain py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <Eyebrow className="justify-center">Our Work</Eyebrow>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-slate-900 tracking-tight leading-tight">
              Rooms we've helped transform
            </h1>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-slate-600">
              A gallery of the styles we design, measure and fit. We're
              currently photographing recent installations — genuine project
              images will be added here shortly.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 mb-10" role="group" aria-label="Filter projects">
            {projectFilters.map((f) => (
              <button
                key={f}
                type="button"
                data-testid={`filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setFilter(f)}
                aria-pressed={filter === f}
                className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-all duration-200 ${
                  filter === f
                    ? "border-navy-900 bg-navy-900 text-white"
                    : "border-warm-300 bg-white text-slate-700 hover:border-navy-800/50"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {visible.map((p) => (
                <motion.figure
                  layout
                  key={p.title + p.room}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.35 }}
                  className="group overflow-hidden rounded-2xl border border-warm-200 bg-white shadow-sm"
                >
                  <div className="overflow-hidden aspect-[4/3]">
                    <img
                      src={p.image}
                      alt={`${p.title} in a ${p.room.toLowerCase()}`}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <figcaption className="p-5">
                    <p className="font-serif text-xl text-slate-900">{p.title}</p>
                    <p className="text-xs uppercase tracking-[0.15em] text-slate-500 mt-1">
                      {p.room} · {p.product}
                    </p>
                  </figcaption>
                </motion.figure>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <CTASection
        title="Like what you see? Let's do yours."
        text="Book a free design visit and we'll help you choose the right style for every window in your home."
        source="our_work_cta"
      />
    </>
  );
}
